import { Header } from 'components/Header';
import { Nav } from 'components/Nav';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './Layouts.module.scss';

export const ScrollToTopContext = createContext<{ scrollToTop: () => void } | null>(null);
export const useScrollToTop = () => useContext(ScrollToTopContext);
import { Box } from '@mantine/core';
import { NavMobile } from 'components/Nav/NavMobile';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logOut } from 'redux/auth/slice';
import { useMediaQuery } from '@mantine/hooks';
import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import { GeneralNotification } from '../../components/GeneralNotification/GeneralNotification';
import { RootState } from 'redux/store';
import { AlertEventPayload } from 'redux/webSocket/slice';

let hasLoadedMessages = false;
let persistentUnreadMessagesOrderChat = 0;
let persistentUnreadMessagesSupport = 0;
let lastPath = '';

const isProduction = process.env.NODE_ENV === 'production';

type Props = {
  children: ReactNode;
  setOpenNav?: (value: boolean | undefined) => void;
};

export const Layout = ({ children, setOpenNav }: Props) => {
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const workerScrollRef = useRef<HTMLDivElement>(null);
  const mobileTopBarRef = useRef<HTMLDivElement>(null);
  const [mobileTopBarHeight, setMobileTopBarHeight] = useState(0);
  const scrollToTop = useCallback(() => {
    const opts = { top: 0, behavior: 'smooth' } as const;
    workerScrollRef.current?.scrollTo(opts);
    contentScrollRef.current?.scrollTo(opts);
    window.scrollTo(opts);
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [unreadMessagesSupport, setUnreadMessagesSupport] = useState(
    persistentUnreadMessagesSupport,
  );
  const [unreadMessagesOrderChat, setUnreadMessagesOrderChat] = useState(
    persistentUnreadMessagesOrderChat,
  );
  const [chatToken, setChatToken] = useState<string | null>(
    localStorage.getItem('chatToken'),
  );
  const events = useSelector((state: RootState) => state.websocket);
  const location = useLocation();
  const isChatRoute = location?.pathname?.includes('chat');
  const isMobile = useMediaQuery(`(width < 768px)`);

  const disableClassName = isChatRoute && isMobile;

  const cockieLifeTime = Number(process.env.REACT_APP_ALERTS_COOLDOWN_SECONDS);

  const [activeNotifications, setActiveNotifications] = useState<any[]>([]);

  const hideNotificationsRoutes = ['/chat', '/knowledge', '/chat/', '/knowledge/'];
  const shouldHideNotifications = hideNotificationsRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  useEffect(() => {
    const notifications = events
      .filter((event): event is AlertEventPayload => event != null)
      .map((event) => ({
        id: event.id,
        title: event.title,
        body: event.text,
        generalType: true,
        countTime: cockieLifeTime,
      }));

    setActiveNotifications(notifications);
  }, [events]);

  const logout = () => {
    const token = localStorage.getItem('token');
    if (token) {
      localStorage.removeItem(`alertsLoaded_${token}`);
    }
    navigate('/login', { replace: true });
    localStorage.removeItem('token');
    localStorage.removeItem('chatToken');
    localStorage.removeItem('role');
    dispatch(logOut());
  };

  const fetchChatToken = async () => {
    if (!isProduction) {
      return null;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/get-access-token`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: getAuthorizationHeader(),
          },
        },
      );
      const data = await res.json();

      localStorage.setItem('chatToken', data.token);
      setChatToken(data.token);
      return data.token;
    } catch (error) {
      console.log('Ошибка при получении токена:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeChatToken = async () => {
      if (!isProduction) {
        return;
      }

      const storedToken = localStorage.getItem('chatToken');
      if (storedToken) {
        setChatToken(storedToken);
        return;
      }

      await fetchChatToken();
    };
    initializeChatToken();
  }, []);

  const fetchUnreadMessages = async (
    retryOnUnauthorized = true,
    tokenOverride = null,
  ) => {
    if (!isProduction) {
      return;
    }

    const currentChatToken =
      tokenOverride || chatToken || localStorage.getItem('chatToken');

    if (!currentChatToken) {
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_CHAT_URL}/api/messages/count-unread-messages`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${currentChatToken}`,
          },
        },
      );

      if (res.status === 401 && retryOnUnauthorized) {
        const newToken = await fetchChatToken();
        if (newToken) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return fetchUnreadMessages(false, newToken);
        }
        return;
      }

      const responseData = await res.json();

      persistentUnreadMessagesSupport = responseData.count || 0;
      setUnreadMessagesSupport(persistentUnreadMessagesSupport);

      persistentUnreadMessagesOrderChat = responseData.customer_worker_count || 0;
      setUnreadMessagesOrderChat(persistentUnreadMessagesOrderChat);
    } catch (error) {
      console.log('Ошибка при получении непрочитанных сообщений:', error);
    }
  };

  useEffect(() => {
    if (!chatToken || !isProduction) return;

    if (!hasLoadedMessages) {
      fetchUnreadMessages();
      hasLoadedMessages = true;
    }
  }, [chatToken]);

  useEffect(() => {
    if (!isProduction) return;

    if (lastPath.includes('chat') && !location.pathname.includes('chat')) {
      setTimeout(() => {
        fetchUnreadMessages();
      }, 300);
    }

    lastPath = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!isProduction) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !location.pathname.includes('chat')) {
        fetchUnreadMessages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, chatToken]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'role' && e.key !== 'token') return;
      if (e.newValue === e.oldValue) return;

      if (!localStorage.getItem('token') || !localStorage.getItem('role')) {
        logout();
        return;
      }

      window.location.reload();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const el = mobileTopBarRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setMobileTopBarHeight(Math.ceil(rect.height));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isMobile]);

  return (
    <ScrollToTopContext.Provider value={{ scrollToTop }}>
      <div
        ref={workerScrollRef}
        className={!disableClassName ? styles.worker : ''}
        data-scroll-container={!disableClassName ? 'main' : undefined}>
        {isMobile ? null : (
          <Nav
            unreadMessagesSupport={unreadMessagesSupport}
            unreadMessagesOrderChat={unreadMessagesOrderChat}
            setOpenNav={setOpenNav}
          />
        )}
        <div
          ref={contentScrollRef}
          className={!disableClassName ? styles.content : ''}
          style={
            isMobile
              ? ({ ['--topbar-height' as any]: `${mobileTopBarHeight}px` } as any)
              : undefined
          }
          data-scroll-container={!disableClassName ? 'content' : undefined}>
          {isMobile ? (
            <div ref={mobileTopBarRef} className={styles.mobileTopBar}>
              <NavMobile
                unreadMessagesSupport={unreadMessagesSupport}
                unreadMessagesOrderChat={unreadMessagesOrderChat}
              />
              <Header />
            </div>
          ) : (
            <Header />
          )}
          <Box m={{ base: '0 16px', sm: '20px 40px' }}>
            {!shouldHideNotifications && activeNotifications.length > 0
              ? activeNotifications.map((item) => (
                  <GeneralNotification key={item.id} data={item} styles={'general'} />
                ))
              : null}
            {children}
          </Box>
        </div>
      </div>
    </ScrollToTopContext.Provider>
  );
};
